import { API_VERSION } from '../hooks/useAxios'
import { VERSION } from '../version'

/**
 * Number of websocket `update` messages this page has processed (see `useSocket`).
 * Counted here, at module level, so the count is available to the error report
 * without the reporter depending on any React state.
 */
let websocketUpdates = 0
window.addEventListener('websocket-update', () => { websocketUpdates++ })

/**
 * Chrome-only memory figures. Firefox and Safari expose neither, which is why the
 * report also carries the engine-independent counts below.
 */
type MemoryInfo = { usedJSHeapSize: number, totalJSHeapSize: number, jsHeapSizeLimit: number }
type PerformanceWithMemory = Performance & { memory?: MemoryInfo }
type NavigatorWithDeviceMemory = Navigator & { deviceMemory?: number }

/**
 * Facts about the page's state that make a stackless error diagnosable, e.g. a
 * Firefox `out of memory` that names no code: how long the page has been open,
 * how much live traffic it has seen, and how large the page's structures have
 * grown. Every field is optional and every read is guarded — this runs while
 * the page may already be failing, so it must never throw itself.
 *
 * - `uptime_s`: seconds since navigation (the tab's age).
 * - `websocket_updates`: live `update` messages processed by this page.
 * - `dom_elements`, `css_rules`: what a DOM or style-sheet leak inflates; the
 *   RelayBar leak of 2026-09 would have shown up as `css_rules` in the hundreds
 *   of thousands.
 * - `local_storage_kb`: total size of this origin's local storage (the device
 *   state is rewritten there on every update).
 * - `memory`: Chrome's JS heap figures and device memory, when the browser has them.
 */
export function pageDiagnostics(): Record<string, unknown> {
  const diagnostics: Record<string, unknown> = {}
  const attempt = (name: string, read: () => unknown): void => {
    try {
      const value = read()
      if (value !== undefined && value !== null) {
        diagnostics[name] = value
      }
    } catch (err) {
      // A failing probe is left out; the report is still worth sending.
    }
  }

  attempt('uptime_s', () => Math.round((Date.now() - performance.timeOrigin) / 1000))
  attempt('websocket_updates', () => websocketUpdates)
  attempt('dom_elements', () => document.getElementsByTagName('*').length)
  attempt('css_rules', () => [...document.styleSheets].reduce((count, sheet) => {
    try {
      return count + sheet.cssRules.length
    } catch (err) {
      return count
    }
  }, 0))
  attempt('local_storage_kb', () => {
    let chars = 0
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i) || ''
      chars += key.length + (localStorage.getItem(key) || '').length
    }
    return Math.round(chars / 1024)
  })
  attempt('memory', () => {
    const memory = (performance as PerformanceWithMemory).memory
    const deviceMemory = (navigator as NavigatorWithDeviceMemory).deviceMemory
    if (!memory && deviceMemory === undefined) {
      return undefined
    }
    return {
      ...(memory
        ? {
            used_js_heap_mb: Math.round(memory.usedJSHeapSize / 1048576),
            total_js_heap_mb: Math.round(memory.totalJSHeapSize / 1048576),
            js_heap_limit_mb: Math.round(memory.jsHeapSizeLimit / 1048576),
          }
        : {}),
      ...(deviceMemory !== undefined ? { device_memory_gb: deviceMemory } : {}),
    }
  })

  return diagnostics
}

/**
 * Report a client-side error to the backend, which logs it and raises a Slack
 * alert (see SunergosWeb.ExceptionController.alert/2).
 *
 * Used both by the global window 'error' listener below and by explicit guards
 * that handle a problem gracefully (so it never reaches window 'error') but still
 * want it surfaced to the team — e.g. refusing to flash an incomplete appliance
 * config.
 */
export function reportClientError(title: string, stack: string = ''): void {
  // Resolve plugins if possible.
  const plugins: string[] = []

  if (navigator && navigator.plugins) {
    for (let i = 0; i < navigator.plugins.length; i++) {
      plugins.push(navigator.plugins[i].name)
    }
  }

  try {
    // Do raw posting to be independent of library errors.
    const url = `/api/${API_VERSION}/exceptions`
    const xhr = new XMLHttpRequest()

    xhr.open('POST', url)
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest')
    xhr.setRequestHeader('Content-Type', 'application/json')
    xhr.onload = function() {
      if (xhr.status !== 204) {
        console.error('Failed to log JS error.' + xhr.status)
      }
    }
    xhr.send(JSON.stringify({
      title,
      stack,
      plugins,
      token: localStorage.getItem('token'),
      browser: navigator.userAgent,
      url: document.location.href,
      version: VERSION,
      diagnostics: pageDiagnostics(),
      why: 'Because Enion Rules',
    }))

  } catch (err) {
    console.error('We got error in error handler:', err)
  }
}

/**
 * Stack frame sources that are never our code: browser extension content scripts
 * and injected user scripts. WebKit runs WKUserScript injections (iOS Safari and
 * Brave extensions) in the page's own world, so when one of them throws, the page
 * gets a normal 'error' event with a full stack instead of an opaque
 * 'Script error.' — the frames just name a source we do not serve, such as
 * `user-script:17`. Chromium and Firefox use their own `*-extension://` schemes.
 */
const FOREIGN_SOURCE = /user-script:\d|[\w-]+-extension:\/\/|webkit-masked-url:\/\//

/**
 * A stack frame that points at a script we actually serve: the production bundle
 * (`/app/assets/index-*.js`) or a Vite module in development (`/app/src/*.tsx`,
 * `/app/node_modules/...js`). Anything else in a frame source is not our code:
 * we ship no inline scripts (frames blamed on the document URL, like
 * `@https://app.enion.fi/app/login:415:45`, are scripts some browser injected
 * into the page) and no eval/new Function (frames with an empty source, like
 * `Pk@`, are scripts evaluated into the page without a source URL — iOS Chrome
 * does this with its own feature scripts on every page).
 */
const OWN_SCRIPT = new RegExp(
  document.location.origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '/app/\\S*\\.(m?js|[jt]sx?)\\b'
)

/**
 * Decide whether a window 'error' event is worth alerting the team about.
 *
 * Exported for testing; the listener below is the only caller.
 */
export function shouldIgnoreError(message: string, stack: string): boolean {
  // Ignore global code.
  if (/^\s*global code@/.test(stack)) {
    return true
  }

  // Ignore errors thrown by extensions / injected user scripts: nothing in them is
  // ours to fix. Only when no frame points at a script we serve — an extension that
  // trips up our own code still leaves our bundle in the stack, and that we want.
  if (FOREIGN_SOURCE.test(stack) && !stack.includes(document.location.origin)) {
    return true
  }

  // Ignore stacks in which no frame names a script we serve. Since we ship no
  // inline scripts and never eval, every frame of ours carries the bundle (or
  // Vite module) URL — a stack made purely of empty-source, native, document-URL
  // or other-origin frames is code someone injected into the page, most commonly
  // iOS Chrome's own feature scripts (2026-09-04: `Pk@`/`Nk@` recursion blowing
  // the stack on /app/login and /app/home, zero frames of ours). An injected
  // script that trips up our own code still leaves our bundle in the stack, and
  // that we keep. Empty stacks fall through: with no frames at all we cannot
  // tell whose the error is, so we err on reporting.
  if (stack.trim() !== '' && !OWN_SCRIPT.test(stack)) {
    return true
  }

  // Ignore some known cases.
  return `${message}`.toLocaleLowerCase() === 'script error.' || isForeignDomMutation(message, stack)
}

/**
 * Message of a DOM NotFoundError raised by removeChild/insertBefore, in the words of each engine:
 *
 * - Firefox: `NotFoundError: Node.removeChild: The node to be removed is not a child of this node`
 * - Chrome: `NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be removed is
 *   not a child of this node.`
 * - Safari: `NotFoundError: The object can not be found here.` (the method is only in the stack,
 *   as `removeChild@[native code]`)
 */
const DOM_NOT_FOUND = /^\s*notfounderror\b/i
const DOM_MUTATION_METHOD = /\b(removeChild|insertBefore)\b/

/**
 * React throwing NotFoundError from removeChild/insertBefore during its commit means a DOM node it
 * owns is no longer where React last put it: something outside React rearranged the page. Our code
 * never moves or removes DOM nodes itself (no appendChild/removeChild/innerHTML in src/), so the
 * culprit is a browser feature or extension rewriting the page under React — in practice
 * translation, which replaces text nodes with translated ones (Google Translate wraps them in
 * `<font>`, Firefox Translations rebuilds the inline subtree). The next React update that removes
 * one of the original nodes then fails here. Nothing in it is ours to fix, so it is not reported.
 *
 * Safari's phrasing has been ignored since SW-2178 (2025-03); the Firefox one hit prod on
 * 2026-09-24 (v0.162.572, /app/home, stack entirely react-dom commit-phase recursion).
 */
function isForeignDomMutation(message: string, stack: string): boolean {
  return DOM_NOT_FOUND.test(message) && DOM_MUTATION_METHOD.test(`${message}\n${stack}`)
}

/**
 * Independent error handler posting errors to API.
 */
window.addEventListener('error', function (event) {

  const { error } = event

  if (error?.stack?.indexOf('invokeGuardedCallbackDev') >= 0 && !error.alreadySeen) {
    error.alreadySeen = true
    event.preventDefault()
    return
  }

  // Ignore incomplete errors.
  if (!error) {
    return
  }

  let title = event.message

  const stack = error ? event?.error?.stack || '' : ''
  if (!stack) {
    title = `Unable to find stack from the error: ${error} (${JSON.stringify(event.message)})`
  } else if (!title) {
    title = `Unable to find message content from the event: ${event}`
  }

  if (shouldIgnoreError(event.message, stack)) {
    return
  }

  reportClientError(title, stack)

}, { capture: true })
